// Driller — boot, fixed-step sim loop, render loop, bus wiring.
import { SPEEDS, dayToDate } from './core/time.js';
import { on } from './core/bus.js';
import { api, loadApi, callAction } from './ui/api.js';
import { $, date } from './ui/dom.js';
// perf(load): the game shell and every panel load lazily (dynamic import) so the title screen boots from a small
// core — title.js + api.js; the bundler (scripts/build.js) turns each boundary into a chunk. See docs/PERFORMANCE.md.
import * as front from './ui/title.js';
import { createGame } from './ui/newGame.js';
import { pwa } from './ui/pwa.js';
import { perf } from './ui/perf.js'; // render-perf: HUD (?perf / F3 / Settings) — tiny, static on purpose
let topbar, nav, ticker, toast, applyEraNames, modals, onboarding, newspaper, m2modals, m3modals, m4modals, markAccident; // M4-UI: m4modals
const loadShell = () => import('./ui/shell.js').then((m) => { ({ topbar, nav, ticker, toast, applyEraNames, modals, onboarding, newspaper, m2modals, m3modals, m4modals, markAccident } = m); });
// A panel stub: same surface as a panel module (mount/show/render/frame/reset); forwards once its chunk is in.
function lazyPanel(load) {
  let mod = null, promise = null, host = null, hostCtx = null, shown = null;
  const self = {
    load: () => (promise ??= load().then((m) => {
      mod = m;
      if (host) { host.textContent = ''; m.mount(host, hostCtx); if (shown && hostCtx.route === host.dataset.panel) m.show?.(shown, hostCtx.state()); hostCtx.dirty(); }
      return m;
    }).catch((e) => { promise = null; if (host) host.innerHTML = `<div class="boot"><p>Couldn't load this panel (offline?).</p><pre class="small mono">${String(e?.message ?? e).replace(/</g, '&lt;')}</pre></div>`; throw e; })),
    mount(el, c) { host = el; hostCtx = c; if (mod) return mod.mount(el, c); el.innerHTML = '<div class="boot"><p>Loading…</p></div>'; self.load().catch((e) => console.error('[panel]', el.dataset.panel, e)); },
    show(p, s) { if (mod) return mod.show?.(p, s); shown = p; },
    render: (s) => mod?.render?.(s),
    frame: (s, t) => mod?.frame?.(s, t),
    reset: (s) => mod?.reset?.(s),
  };
  return self;
}
const PANELS = {
  map: lazyPanel(() => import('./ui/map.js')),
  field: lazyPanel(() => import('./ui/field.js')),
  well: lazyPanel(() => import('./ui/well.js')),
  fleet: lazyPanel(() => import('./ui/fleet.js')),
  contracts: lazyPanel(() => import('./ui/contracts.js')),
  ventures: lazyPanel(() => import('./ui/ventures.js')), // M3: lease rounds, licences, state participation, megaprojects
  refinery: lazyPanel(() => import('./ui/refinery.js')),
  shop: lazyPanel(() => import('./ui/shop.js')),
  crew: lazyPanel(() => import('./ui/crew.js')),
  research: lazyPanel(() => import('./ui/research.js')),
  market: lazyPanel(() => import('./ui/market.js')),
  finance: lazyPanel(() => import('./ui/finance.js')),
  log: lazyPanel(() => import('./ui/log.js')),
  tokens: lazyPanel(() => import('./ui/tokens.js')),
  wardrobe: lazyPanel(() => import('./ui/wardrobe.js')), // cosmetics: rig skins & crew uniforms
  almanac: lazyPanel(() => import('./ui/almanac.js')), // almanac: Oilfield Almanac (glossary handbook)
};
// perf(load) end
const DOM_HZ = 4;
const MAX_TICKS_PER_FRAME = 30;
const AUTOSAVE_REAL_MS = 45000;

let state = null;
let dirty = true;
let current = 'map';
const mounted = {};
const params = { route: 'map' };

const params0 = new URLSearchParams(location.search);
const FORCE_MOCK = params0.has('mock');
// Title screen on every load; ?play skips it (and ?mock does too unless ?title is also given, for dev scripts).
const SKIP_TITLE = params0.has('play') || (FORCE_MOCK && !params0.has('title'));

const ctx = {
  api,
  state: () => state,
  setState(s) { state = s; notifyDefaults(s); ctx.dirty(); for (const p of Object.values(PANELS)) p.reset?.(s); newspaper.reset(); toast?.reset?.(); modals?.resetDecisions?.(); },
  dirty() { dirty = true; },
  // act(fn | 'actionName', ...args) -> result. Shows a toast with msg.
  act(fn, ...args) {
    const r = callAction(state, fn, ...args);
    if (r.msg) toast.show(r.msg, r.ok ? (r.good ? 'good' : '') : 'bad', null, { feed: false }); // replies to your own click: not log material
    if (!r.ok) sound?.ui('toast', { kind: 'bad' }); else if (r.good) sound?.ui('toast', { kind: 'good' });
    if (r.ok && (fn === 'sellOil' || fn === api.actions.sellOil)) sound?.ui('kaching');
    dirty = true;
    onboarding.check(state, ctx);
    return r;
  },
  // Like act but silent on success.
  quiet(fn, ...args) {
    const r = callAction(state, fn, ...args);
    if (!r.ok && r.msg) toast.show(r.msg, 'bad', null, { feed: false });
    dirty = true;
    return r;
  },
  go(route, p = {}) {
    if (!PANELS[route]) return;
    Object.assign(params, p, { route });
    current = route;
    for (const [k, el] of Object.entries(mounted)) el.hidden = k !== route;
    ensureMounted(route);
    PANELS[route].show?.(p, state);
    nav.setActive(route);
    dirty = true;
    renderDom(true);
    onboarding.check(state, ctx);
    try { sessionStorage.setItem('driller.route', route); } catch {}
  },
  get route() { return current; },
  params,
  // shell modules (toast, modals) load lazily, so read the live bindings at call time and pass the action through
  toast: (m, k, a, o) => toast?.show(m, k, a, o),
  // the first well, under the first-steps hints: routine notices (shift reports, front pages, achievements) go to the log only
  firstWellQuiet: () => !!state && !!onboarding?.firstWell(state),
  get modal() { return modals; },
  running: () => !!state && !state.paused && (SPEEDS[state.speed] ?? 0) > 0,
  setSpeed(n) {
    if (!state) return;
    if (ctx.drill && state.paused && n > 0 && n === state.speed) { state.paused = false; accum = 0; dirty = true; return; } // un-pausing on the brake keeps you on it
    if (ctx.drill) ctx.stopDrill('back to normal speed');
    if (api.actions.setSpeed) callAction(state, 'setSpeed', n);
    else { state.speed = n; state.paused = n === 0; }
    if (n > 0) state.paused = false;
    accum = 0;
    dirty = true;
  },
  togglePause() {
    if (!state) return;
    if ((state.paused || !state.speed) && ctx.drill && state.speed) { state.paused = false; accum = 0; } // on the brake: resume driller's time, don't hand the brake back
    else if (state.paused || !state.speed) ctx.setSpeed(state.speed || lastSpeed || 1);
    else { lastSpeed = state.speed; if (api.actions.togglePause) callAction(state, 'togglePause'); else state.paused = true; state.paused = true; }
    dirty = true;
  },
  pause() { if (state && !state.paused) { lastSpeed = state.speed || lastSpeed; state.paused = true; dirty = true; } },
  resume() { if (state?.paused && !state.gameOver) { if (ctx.drill && state.speed) { state.paused = false; accum = 0; dirty = true; } else ctx.setSpeed(state.speed || lastSpeed || 1); } },
  save(slot = 'auto') {
    try { api.save.saveGame?.(state, slot); return true; } catch (e) { console.error(e); toast.show('Save failed: ' + e.message, 'bad'); return false; }
  },
  newGame(opts) {
    muteBus = true; // polish: setup events (Quick Start wells "completed") are not news
    let s; try { s = createGame(api, opts); } finally { muteBus = false; }
    ctx.setState(s);
    onboarding.restart(s, ctx);
    ctx.go((s.startYear ?? 1859) <= 1859 ? 'field' : 'map');
  },
  // Full front-door flow (charter + prologue) over the running game; used by the Menu's "New game…".
  async newGameFlow() {
    ctx.pause();
    muteBus = true;
    let s; try { s = await front.newGameFlow(api); } finally { muteBus = false; }
    if (!s) return;
    s.paused = true; // like boot: the player sees the map before time runs
    ctx.setState(s);
    onboarding.restart(s, ctx);
    ctx.go((s.startYear ?? 1859) <= 1859 ? 'field' : 'map');
    ctx.save('auto');
  },
};
let lastSpeed = 1;
let accum = 0;
let sound = null;

// ---------- driller's time ("Take the brake" on the console) ----------
// A slowed clock while you drive one drilling well yourself: the sim still ticks whole days (economy, saves and
// determinism unchanged); the Well view interpolates depth through the day and the top bar shows the hour.
const DRILL_DPS = [1 / 45, 1 / 15]; // game days per real second at the slowest: driller's time (45 s a day), ×3
// The clock is paced by footage, not days: about DRILL_FTPS feet of hole per real second. A spring pole making 3½ ft
// a day gets a ~3 s day (Drake's 69 ft in about a minute and a half); a rotary rig making hundreds of feet a day stays
// at the 45 s floor. Never faster than DRILL_DAY_MIN_S seconds a day, so there's still time to work the brake.
const DRILL_FTPS = 1.0, DRILL_DAY_MIN_S = 3;
function drillDps(d) {
  const ftPerDay = d.rate > 0 ? d.rate : d.guess ?? 30; // until the first full day is learned: a guess by rig type
  const base = Math.min(1 / DRILL_DAY_MIN_S, Math.max(DRILL_DPS[0], DRILL_FTPS / ftPerDay));
  return d.fast ? Math.min(1, base * 3) : base;
}
const shiftMark = (w) => ({ day: state.day + accum, depth: w.depth, cost: w.cost ?? 0, bitUid: w.bitUid ?? null, bit: w.bitUid ? state.items[w.bitUid]?.condition ?? null : null });
Object.assign(ctx, {
  drill: null,
  startDrill(wellId) {
    const w = state?.wells?.[wellId];
    if (!w || w.status !== 'DRILLING') { toast?.show('Take the brake while the well is drilling.', 'warn', null, { feed: false }); return false; }
    ctx.drillDeclined = null;
    api.actions.takeBrake?.(state, wellId); // clears 'waiting for you on the brake' at once
    const hist = w.history ?? [], h1 = hist.at(-1), h0 = hist.at(-2); // seed the pace from the last day's footage
    const seedRate = h1 && h0 && h1[0] > h0[0] && h1[1] > h0[1] ? (h1[1] - h0[1]) / (h1[0] - h0[0]) : null;
    const rotary = w.rigType === 'rotary';
    const guess = rotary ? 250 : (state.day < 15000 ? 10 : 25); // spring pole / early cable ~10 ft/d, later cable ~25, rotary hundreds
    ctx.drill = { wellId, fast: false, start: shiftMark(w), warn: 0, incidents: 0, rop: 0, rate: seedRate, guess, prevDay: state.day, prevDepth: w.depth, shown: null, bands: null, now: null, dayT: 0, dayIn: 0, dayBounce: 0, totT: 0, totIn: 0, bandKey: null };
    if (state.paused || !state.speed) { state.speed = state.speed || 1; state.paused = false; }
    accum = 0; dirty = true;
    return true;
  },
  stopDrill(why = '', { auto = false } = {}) {
    const d = ctx.drill; if (!d) return;
    ctx.drill = null; dirty = true;
    if (!auto) ctx.drillDeclined = d.wellId; // you stepped away: don't grab the brake again until you come back
    if (auto && ctx.running()) ctx.setSpeed(1); // the well stopped drilling (strike, trip, casing…): carry on at normal speed rather than race on at the old one
    const w = state?.wells?.[d.wellId]; if (!w) return;
    const ft = Math.max(0, w.depth - d.start.depth), days = Math.max(0, state.day + accum - d.start.day), cost = Math.max((w.cost ?? 0) - d.start.cost, (d.perDay ?? 0) * days);
    const bit = w.bitUid && w.bitUid === d.start.bitUid && d.start.bit != null ? d.start.bit - (state.items[w.bitUid]?.condition ?? d.start.bit) : null;
    accum = 0;
    if (days < 0.15 && ft < 1) return; // barely started
    const dur = days < 1 ? `${Math.max(1, Math.round(days * 24))} h` : `${days.toFixed(1)} days`;
    const parts = [`${Math.round(ft)} ft in ${dur}`, ft >= 1 ? `$${(cost / ft).toFixed(2)}/ft` : null, bit != null ? `bit −${Math.max(0, Math.round(bit))}%` : null,
      d.totT > 0.05 ? `in the sweet spot ${Math.round(100 * d.totIn / d.totT)}%` : null,
      d.warn ? `${d.warn} warning${d.warn === 1 ? '' : 's'} ridden out` : null, d.incidents ? `${d.incidents} incident${d.incidents === 1 ? '' : 's'}` : null].filter(Boolean);
    toast?.show(`Shift report — ${parts.join(' · ')}${why ? ` (${why})` : ''}`, d.incidents ? 'warn' : 'good', null, { quiet: auto || ctx.firstWellQuiet() }); // the well stopped by itself, or the first well: log only
  },
  toggleDrillFast() { if (ctx.drill) { ctx.drill.fast = !ctx.drill.fast; dirty = true; } },
  drillFrac: () => (ctx.drill ? Math.min(0.999, Math.max(0, accum)) : 0),
  // Depth shown for the driven well. It replays the hole the sim really made: the day just simulated is animated
  // over the next real-time day (from → to), so the display never shows hole that wasn't drilled and never goes
  // back up the hole. Before the first simulated day it makes a cautious guess (and holds if that ran ahead).
  drillShown(wellId) {
    const d = ctx.drill, w = state?.wells?.[wellId];
    if (!w) return 0;
    if (!d || d.wellId !== wellId || w.status !== 'DRILLING') return w.depth;
    const frac = ctx.drillFrac();
    const guess = d.to == null
      ? w.depth + Math.max(0, Math.min((w.target ?? Infinity) - w.depth, (d.rop || 0) * 0.5 * frac))
      : d.from + (d.to - d.from) * frac;
    d.shown = Math.max(d.shown ?? 0, guess);
    return d.shown;
  },
  drillExtra: (wellId) => ctx.drillShown(wellId) - (state?.wells?.[wellId]?.depth ?? 0), // may be negative (replay lags)
});
// driller's touch: is the weight in the sweet spot for the rock at the bit (depth moves through the day)?
const WOB_NAME = (w) => (w < 0.8 ? 'Light' : w < 1.15 ? 'Normal' : w < 1.45 ? 'Heavy' : 'Ram it');
function drillTrack(dDays) {
  const d = ctx.drill, w = state?.wells?.[d?.wellId];
  if (!d || !w || !d.bands?.length || w.status !== 'DRILLING') return;
  const dd = w.depth + ctx.drillExtra(d.wellId);
  const b = d.bands.find((x) => dd >= x.top && dd < x.bottom) ?? d.bands[0];
  const wob = w.wob ?? 1, inBand = wob >= b.lo - 1e-3 && wob <= b.hi + 1e-3, bounce = b.bounceAt != null && wob >= b.bounceAt - 1e-3;
  d.dayT += dDays; d.totT += dDays; if (inBand) { d.dayIn += dDays; d.totIn += dDays; } if (bounce) d.dayBounce += dDays;
  d.now = { name: b.name, hard: b.hard, lo: b.lo, hi: b.hi, best: b.best, wob, inBand, bounce, tooHard: b.tooHard, loName: WOB_NAME(b.lo), hiName: WOB_NAME(b.hi) };
  const key = `${b.top}|${b.name}`;
  if (d.bandKey && key !== d.bandKey) { // the bit has crossed into a new rock
    const call = wob > b.hi ? 'ease off' : wob < b.lo ? 'more weight' : 'hold it there';
    const { loName, hiName } = d.now;
    if (call !== 'hold it there' || b.tooHard) toast?.show(`Into the ${b.name} (h${b.hard}) — ${call}: sweet spot ${loName === hiName ? loName : `${loName}–${hiName}`}${b.tooHard ? '. Too hard for this bit!' : ''}`, wob > b.hi || b.tooHard ? 'warn' : 'info', null, { feed: false }); // driller's-time coaching: not log material
    d.newRockAt = performance.now();
  }
  d.bandKey = key;
}
// before each simulated day: hand the sim today's touch (share in the sweet spot, share bouncing)
function drillCommit() {
  const d = ctx.drill; if (!d) return;
  const q = d.dayT > 0 ? d.dayIn / d.dayT : 0, bounce = d.dayT > 0 ? d.dayBounce / d.dayT : 0; // driving at all counts as driving the day
  try { api.actions.setDriveTouch?.(state, d.wellId, q, bounce); } catch (e) { console.error('[drill touch]', e); }
  d.dayT = d.dayIn = d.dayBounce = 0;
}
function drillWatch() { // leave driller's time when the well stops drilling or you leave its Well screen
  const d = ctx.drill;
  if (!d) { // hands-on drilling: watching a drilling well with the clock running puts you on the brake
    if (current !== 'well') { ctx.drillDeclined = null; return; }
    const id = ctx.viewWellId, w = id && state?.wells?.[id];
    if (ctx.drillDeclined && ctx.drillDeclined !== id) ctx.drillDeclined = null;
    if (state?.settings?.handsOn && w && w.status === 'DRILLING' && ctx.running() && ctx.drillDeclined !== id && !modals?.blocking?.()) ctx.startDrill(id);
    return;
  }
  const w = state?.wells?.[d.wellId];
  if (!w) return ctx.stopDrill();
  if (w.status !== 'DRILLING') return ctx.stopDrill(`${w.name} is ${w.status.toLowerCase().replace(/_/g, ' ')}`, { auto: true });
  if (current !== 'well') return ctx.stopDrill('left the Well screen');
}

function ensureMounted(route) {
  if (mounted[route]) return;
  const el = document.createElement('section');
  el.className = 'panel-host';
  el.dataset.panel = route;
  $('#main').appendChild(el);
  mounted[route] = el;
  PANELS[route].mount(el, ctx);
}

// ---------- loops ----------
let lastTick = performance.now();
function simStep() {
  const now = performance.now();
  const dt = Math.min(0.25, (now - lastTick) / 1000);
  lastTick = now;
  if (!state || !ctx.running() || modals.blocking() || modals.decisionWaiting?.(state) || newspaper.isOpen()) return;
  if (ctx.drill) dirty = true; // the top-bar clock shows hours in driller's time
  const rate = ctx.drill ? drillDps(ctx.drill) : SPEEDS[state.speed];
  if (ctx.drill) drillTrack(Math.min(dt * rate, Math.max(0, 1 - accum)));
  accum += dt * rate;
  let n = Math.floor(accum);
  if (n <= 0) return;
  n = Math.min(n, MAX_TICKS_PER_FRAME);
  accum -= n;
  if (accum > MAX_TICKS_PER_FRAME) accum = 0;
  const pt0 = perf.on ? performance.now() : 0, pb0 = perf.busMs; // render-perf: sim timing (minus UI bus handlers)
  let ran = 0;
  for (let i = 0; i < n; i++) {
    ran++;
    if (ctx.drill) drillCommit(); // driller's time: today's touch goes into the day being simulated
    try { api.tick(state); } catch (e) { console.error('[tick]', e); ctx.pause(); toast.show('Simulation error: ' + e.message, 'bad'); break; }
    if (state.paused || state.gameOver || modals.decisionWaiting?.(state)) break; // a decision came up: stop on its day
  }
  if (perf.on) perf.ticks(ran, performance.now() - pt0, perf.busMs - pb0); // render-perf
  dirty = true;
  if (ctx.drill) { // learn the real daily footage for the depth display
    const d = ctx.drill, w = state.wells[d.wellId];
    if (w && d.prevDay !== state.day) {
      const ft = w.depth - d.prevDepth; if (ft > 0.01 && w.status === 'DRILLING') d.rate = ft;
      d.from = Math.min(d.shown ?? d.prevDepth, Math.max(d.shown ?? d.prevDepth, w.depth)); d.to = w.depth; // replay today's real footage
      d.prevDay = state.day; d.prevDepth = w.depth;
    }
  }
  yearEndNotice();
  drillWatch();
  scheduleAutosave(); // sim-perf: the tick only requests the autosave; write it when the browser is idle
}
// 1 January: a one-line annual result with a link to the Finance annual report (panel code loads on demand).
let lastYearSeen = null, lastYearState = null;
function yearEndNotice() {
  const y = dayToDate(state.day).year;
  if (lastYearState !== state || lastYearSeen == null) { lastYearState = state; lastYearSeen = y; return; } // new game / loaded save
  if (y < lastYearSeen) { lastYearSeen = y; return; }
  if (y === lastYearSeen) return;
  lastYearSeen = y;
  const st = state;
  Promise.all([import('./ui/finance.js'), import('./ui/dom.js')]).then(([fin, dom]) => {
    const a = fin.annualRollup?.(st)?.find((r) => r.year === y - 1);
    if (!a || a.months < 3 || st !== state) return;
    toast?.show(`📒 ${y - 1} annual report: net ${a.net >= 0 ? 'profit' : 'loss'} ${dom.money(Math.abs(a.net))} on ${dom.money(a.income)} income.`, a.net >= 0 ? 'good' : 'bad', { label: 'Annual report', onClick: () => ctx.go('finance') });
  }).catch(() => {});
}
// sim-perf: autosave outside the tick (core/save.js autosave → flushAutosave), after the frame, when idle.
let autosaveQueued = false;
function scheduleAutosave() {
  if (autosaveQueued || !api.save?.autosavePending?.()) return;
  autosaveQueued = true;
  const run = () => { autosaveQueued = false; try { api.save.flushAutosave(state); } catch (e) { console.error('[autosave]', e); } };
  if (typeof requestIdleCallback === 'function') requestIdleCallback(run, { timeout: 3000 }); else setTimeout(run, 0);
}

let lastDom = 0;
function renderDom(force = false) {
  if (!state) return;
  const now = performance.now();
  if (!force && (!dirty || now - lastDom < 1000 / DOM_HZ)) return;
  lastDom = now;
  dirty = false;
  const pd0 = perf.on ? performance.now() : 0; // render-perf: DOM timing
  applyEraNames(api.data.FIELDS, api.data.REGIONS, dayToDate(state.day ?? 0).year); // polish C4: period place names
  drillWatch();
  topbar.render(state);
  nav.render(state);
  ticker.render(state);
  PANELS[current]?.render(state);
  modals.render?.(state);
  newspaper.render(state);
  onboarding.render(state, ctx);
  if (perf.on) perf.add('dom', performance.now() - pd0); // render-perf
}
let lastViewFrame = 0, lastInput = 0; // render-perf
// render-perf: view frame-rate policy. Under an open modal / newspaper (dimmed, backdrop-blurred) ~10 fps — every
// canvas change behind a backdrop-filter forces a full re-blur. Paused with no input for 3 s: ~20 fps (laptops stop
// redrawing a static scene at 60 fps; any input or unpause restores the full rate at once). "Battery" render
// resolution: 30 fps cap. Quality guards ignore throttled frames (perf.throttled).
function viewInterval() {
  if (modals?.isOpen?.() || newspaper?.isOpen?.()) return 100;
  if (perf.probing) return 0;
  if (!ctx.running() && performance.now() - lastInput > 3000) return 50;
  return perf.res === 'battery' ? 33 : 0;
}
function frame(t) {
  const pf0 = perf.on ? (perf.frameStart(t), performance.now()) : 0; // render-perf: frame + view timing
  if (state) {
    const iv = viewInterval(); perf.throttled = iv > 0;
    if (!iv || t - lastViewFrame >= iv - 4) { lastViewFrame = t; PANELS[current]?.frame?.(state, t); }
    if (perf.on) perf.add('view', performance.now() - pf0); // render-perf
    modals.frame?.(t);
    renderDom();
    try { sound?.update(state, soundView()); } catch (e) { console.error('[sound]', e); sound = null; }
  }
  if (perf.on) perf.frameEnd(performance.now() - pf0); // render-perf
  requestAnimationFrame(frame);
}

function soundView() {
  const wellId = current === 'well' ? params.wellId : null;
  return {
    route: current, wellId,
    fieldId: current === 'field' ? params.fieldId : wellId ? state.wells?.[wellId]?.fieldId : null,
    blocking: modals.blocking(),
  };
}

// ---------- bus ----------
// Subscribe per event type (not '*'): the wildcard spreads the payload over `type`, and some payloads
// (accident, warning) carry their own `type` field.
const EVENTS = ['strike', 'dry', 'gusher', 'blowout', 'injury', 'bitWorn', 'storageFull', 'era', 'news', 'achievement', 'gameOver', 'tokens', 'attack', 'invention', 'problem', 'accident', 'warning', 'death', 'contractOffer', 'contractDone', 'contractFailed', 'cashBurn']; // + m2modals/m3modals EVENTS in wireBus (perf(load): lazy shell)
// A usable spare bit waiting in the warehouse (the rig fits it at the start of the next day).
const spareBitInStock = () => Object.values(state?.items ?? {}).some((i) => i.loc?.type === 'warehouse' && api.data.EQUIPMENT?.[i.defId]?.cat === 'bit' && (i.condition ?? 0) > 20);
// Does the player have anything in this field / region? (wells, leased plots, rigs on location, platforms)
function hasStakeIn(regionId, fieldId) {
  const F = api.data.FIELDS ?? {}, inScope = (fid) => fid && (fid === fieldId || (regionId && F[fid]?.regionId === regionId));
  for (const w of Object.values(state.wells ?? {})) if (w.status !== 'PLUGGED' && inScope(w.fieldId)) return true;
  for (const [fid, f] of Object.entries(state.fields ?? {})) if (inScope(fid) && (f.plots ?? []).some((p) => p.owned)) return true;
  for (const r of Object.values(state.rigs ?? {})) if (inScope(r.at?.fieldId)) return true;
  for (const p of Object.values(state.platforms ?? {})) if (inScope(p.fieldId)) return true;
  return false;
}
const AUTO_KEYS = { accident: 'injury', death: 'injury' };
// One-time switch to the notification log (2026-10-31): events stop pausing by default, in new games and old saves
// alike. Menu → Settings can turn the pauses back on.
const PAUSE_KEYS = ['strike', 'dry', 'gusher', 'blowout', 'injury', 'bitWorn', 'storageFull', 'era', 'news', 'attack', 'problem', 'storm'];
function notifyDefaults(s) {
  const st = s ? (s.settings ??= {}) : null;
  if (!st || st.notifyLog) return;
  st.autoPause ??= {};
  for (const k of PAUSE_KEYS) st.autoPause[k] = false;
  st.notifyLog = 1;
}
const startedIn = (eraId) => {
  const e = (api.data.ERAS ?? []).find((x) => x.id === eraId);
  return !!e && (state?.startYear ?? 1859) > 1859 && e.startYear <= state.startYear;
};
// Cards a bus event would open (strike, era, kick, glut…) become a notice with an Open button instead; reading one
// pauses until it's closed (modals.show). Well problems still need attention until the well is clear again.
let cardEv = null;
const plain = (h) => { const d = document.createElement('div'); d.innerHTML = h ?? ''; return d.textContent.replace(/\s+/g, ' ').trim().slice(0, 200); };
function cardNotice(m) {
  const { type, ev } = cardEv ?? {};
  const text = ev?.text || plain(m.hero) || 'Notice';
  const kind = type === 'strike' || type === 'gusher' ? 'good' : /\bbad\b/.test(m.heroCls ?? '') ? 'bad' : '';
  const wid = ev?.wellId;
  const opts = type === 'problem' && wid ? { valid: () => !!state?.wells?.[wid]?.problem } : { needs: false };
  toast.show(text, kind, { label: 'Open', onClick: () => modals.show(m) }, opts);
}
let muteBus = false, holdingFx = false, holdFxTimer = 0; // holdingFx: an incident is playing out before its card
const FX_HOLD_MS = 4000;
const FX_ON_WELL = new Set(['accident', 'blowout', 'gusher', 'kick', 'attack']); // explosions, fires, H₂S, blowouts, gushers, raids, pirates
const FX_ON_FIELD = new Set(['wellFires', 'spill', 'platform']); // field-wide: sabotage fires, oil spills, storm-wrecked platforms
function watchingFx(type, ev) {
  if (current !== 'well' || !ctx.viewWellId) return false;
  const w = state.wells?.[ctx.viewWellId]; if (!w) return false;
  if (FX_ON_WELL.has(type)) return ev.wellId === w.id || (!!ev.rigUid && state.rigs?.[ev.rigUid]?.wellId === w.id);
  if (FX_ON_FIELD.has(type)) {
    if (type === 'platform' && ev.phase !== 'damaged') return false;
    const fid = w.fieldId; return ev.fieldId === fid || ev.wellId === w.id || (ev.fieldIds ?? []).includes(fid) || (!!ev.regionId && api.data.FIELDS?.[fid]?.regionId === ev.regionId);
  }
  return false;
}
function handle(type, ev = {}) {
  if (muteBus) return;
  dirty = true;
  // Contract wells (§17) belong to the operator: their strike / gusher is a toast, not a modal or a pause.
  if ((type === 'strike' || type === 'gusher') && ev.wellId && state?.wells?.[ev.wellId]?.ownerOperatorId) {
    if (ev.text && !ev.completed && !ev.capped) toast.show(ev.text, 'good');
    return;
  }
  // Incidents with an animation on the well you're watching play out before any card or paper covers them.
  if (state && watchingFx(type, ev)) { modals.holdFor?.(FX_HOLD_MS); newspaper.holdFor?.(FX_HOLD_MS + 300); holdingFx = true; clearTimeout(holdFxTimer); holdFxTimer = setTimeout(() => { holdingFx = false; }, FX_HOLD_MS); }
  let pausedNow = false;
  // Regional headlines (a nationalization, a discovery, a local disaster) only stop the game where you have a stake:
  // wells, leases, rigs or platforms in that field or region. World-wide news still pauses. Everything stays in the
  // ticker and the Log; nationalization cards for regions you're not in become a notice.
  if (ev.pause && state && (type === 'news' || type === 'nationalization') && (ev.regionId || ev.fieldId) && !hasStakeIn(ev.regionId, ev.fieldId)) {
    ev = { ...ev, pause: false };
    if (type === 'nationalization') { if (ev.text) toast.show(ev.text); return; }
  }
  // Notices never stop the clock: they go to the log (🔔), and cards wait behind an Open button. Only game over pauses
  // here, plus any pause the player switched on in Settings; must-answer decisions pause when their card opens (modals).
  if (ev.pause && state) {
    const k = AUTO_KEYS[type] ?? type;
    if (type === 'gameOver' || state.settings?.autoPause?.[k] === true) { pausedNow = !state.paused; ctx.pause(); }
  }
  // A Quick Start "enters" the era it starts in on its first tick; the start briefing already covered it.
  if (type === 'era' && startedIn(ev.eraId)) { if (ev.text) toast.show(ev.text, '', null, { quiet: true }); return; }
  const toastsBefore = toast.shown;
  if (ev.text && ['news', 'era', 'achievement'].includes(type)) ticker.push(ev);
  // headlines: in the log, the ticker shows them. Your own money in trouble (RBL deficiency, sweep, Chapter 11) is not a
  // headline: it goes on screen with a Finance button, and a deficiency stays under Needs attention until it's cured.
  const yourMoney = type === 'news' && ev.kind === 'finance' && (ev.level === 'major' || ev.level === 'warn');
  const yourLease = type === 'news' && ev.kind === 'lease' && ev.level === 'warn'; // a lease about to lapse, or lapsed: on screen
  const owing = () => !!state?.m4markets?.rbl?.deficiency, owed = yourMoney && owing();
  if (yourMoney && ev.text) toast.show(`💰 ${ev.text}`, ev.level === 'major' ? 'bad' : 'warn', { label: 'Finance', onClick: () => ctx.go('finance') }, { needs: owed, valid: owed ? owing : null });
  else if (yourLease && ev.text) toast.show(`📜 ${ev.text}`, 'warn', { label: 'Field', onClick: () => ctx.go('field', { fieldId: ev.fieldId }) }, { needs: false });
  else if (type === 'news' && ev.text) toast.show(`📰 ${ev.text}`, ev.level === 'warn' ? 'warn' : '', null, { quiet: true });
  cardEv = { type, ev };
  try { modals.ambient(() => dispatch(type, ev)); } finally { cardEv = null; }
  // Every auto-pause must say why: if this event paused the game and nothing visible appeared, explain it.
  if (pausedNow && !holdingFx && toast.shown === toastsBefore && !modals.isOpen() && !newspaper.isOpen()) {
    const icon = type === 'news' ? '📰' : '⏸';
    toast.show(`${icon} Paused — ${ev.text ?? type}`, type === 'news' ? '' : 'warn', { label: 'Resume', onClick: () => { ctx.togglePause(); renderDom(true); } }, { inbox: false });
  }
}
function dispatch(type, ev) {
  switch (type) {
    case 'strike': modals.strike(ev, ctx); break;
    case 'dry': modals.dry(ev, ctx); break;
    case 'gusher': modals.gusher(ev, ctx); break;
    case 'blowout': modals.blowout(ev, ctx); break;
    case 'problem': if (ev.pause) modals.problem(ev, ctx); else if (ev.text && !quietProblem(ev)) toast.show(ev.text); break;
    case 'era': modals.era(ev, ctx); break;
    case 'gameOver': modals.gameOver(ev, ctx); break;
    case 'attack': modals.attack(ev, ctx); break;
    case 'invention': if (ev.known) toast.show(`💡 ${ev.text}`, 'good'); else modals.invention(ev, ctx); break; // known elsewhere: no decision to make
    case 'accident': markAccident(ev); modals.accident(ev, ctx); break;
    case 'warning': toast.show(ev.text, ev.nearMiss ? '' : 'warn', ev.warningId ? { label: 'Respond', onClick: () => ctx.act('respondWarning', ev.warningId), summary: warnSummary } : null,
      { group: ev.warningId ? 'warn:' + (ev.incidentType === 'tank_fire' ? 'storm' : 'safety') : null, meta: { warningId: ev.warningId },
        valid: () => { const w = state?.safety?.warnings?.[ev.warningId]; return !!w && !w.responded; } }); break;
    case 'achievement': toast.show('🏆 ' + (ev.text ?? 'Achievement unlocked'), 'good', null, { quiet: ctx.firstWellQuiet() }); break; // first well: the strike is the moment
    case 'tokens': if (ev.text) toast.show(ev.text, 'good', null, { quiet: /achievement/i.test(ev.text) }); break; // the 🏆 toast already says it
    case 'contractDone': toast.show(ev.text, 'good'); break;
    case 'contractFailed': toast.show(ev.text, 'bad'); break;
    case 'contractOffer': break; // the nav badge / board shows it
    case 'cashBurn': { const d0 = state?.day ?? 0; toast.show(ev.text, 'bad', { label: 'Review costs', onClick: () => ctx.go('finance') }, { valid: () => (state?.day ?? 0) - d0 < 60 }); break; }
    case 'bitWorn': { // no usable bit: a button to the bit shop; the notice leaves as soon as the well has a bit again
      const wid = ev.wellId;
      if (ev.text) toast.show(ev.text, '', { label: 'Buy bits →', onClick: () => ctx.go('shop', { cat: 'bit' }) }, { valid: () => !!state?.wells?.[wid] && state.wells[wid].problem?.type === ev.problem && !spareBitInStock() });
      break;
    }
    case 'injury': case 'storageFull': case 'death': if (ev.text) toast.show(ev.text, type === 'injury' || type === 'death' ? 'bad' : ''); break;
    default: if (m2modals.handle(type, ev, ctx) || m3modals.handle(type, ev, ctx) || m4modals.handle(type, ev, ctx)) break; // M4-UI: m4modals if (ev.pause && ev.text && type !== 'news') toast.show(ev.text);
  }
}
// polish G4: text for a merged group of warning toasts ("Storms threaten tanks at 2 fields · Respond all $18").
function warnSummary(items) {
  const ws = items.map((it) => state?.safety?.warnings?.[it.meta?.warningId]).filter(Boolean);
  const n = items.length, cost = ws.reduce((a, w) => a + (w.cost ?? 0), 0);
  const c = cost >= 100 ? Math.round(cost) : Math.round(cost * 100) / 100;
  const storm = ws.length && ws.every((w) => w.type === 'tank_fire');
  const nf = new Set(ws.map((w) => w.fieldId)).size || n;
  return { msg: storm ? `Storms threaten tanks at ${nf} field${nf > 1 ? 's' : ''}.` : `${n} safety warnings need a response.`, label: `Respond all · $${c.toLocaleString('en-US')}` };
}
// Routine weather standby ('waiting on weather' as seas hover round a rig's limit) and hands-on 'waiting for you on
// the brake' would toast for every rig, every time: coalesce them into one notice listing the rigs, at most every
// 45 s, and keep each well quiet for 20 game days after its notice. The Log and the Well panel still show everything.
const QUIET_PROBLEMS = { WOW: { label: 'Waiting on weather', days: 20 }, WAITING_DRILLER: { label: 'Waiting for you on the brake', days: 9999 },
  // M4-UI: routine shale-factory notices (frac queue, lift, frac hits) coalesce the same way
  NO_FLEET: { label: 'Frac queue — waiting for a fleet', days: 30 }, RIG_ON_PAD: { label: 'Frac waits for the rig to leave the pad', days: 30 }, NO_SAND: { label: 'Frac waiting for sand', days: 20 }, NO_WATER: { label: 'Frac waiting for water', days: 20 },
  LIFT_INSTALLED: { label: 'Stopped flowing — artificial lift installed', days: 9999 }, LIFT_FAILED: { label: 'Lift failed — workover rig on the way', days: 60 }, NEEDS_LIFT: { label: 'Stopped flowing — needs lift', days: 120 }, FRAC_HIT: { label: 'Frac hit a parent well', days: 60 } };
const quietLast = new Map(), quietBatch = new Map();
let quietTimer = 0, quietAt = 0;
function quietProblem(ev) {
  const q = QUIET_PROBLEMS[ev.problem]; if (!q) return false;
  const k = `${ev.problem}|${ev.wellId ?? ''}`, day = state?.day ?? 0;
  if (day - (quietLast.get(k) ?? -1e9) < q.days) return true;
  quietLast.set(k, day);
  if (ev.problem === 'WAITING_DRILLER' && ctx.drill?.wellId === ev.wellId) return true; // you're already on its brake
  (quietBatch.get(ev.problem) ?? quietBatch.set(ev.problem, []).get(ev.problem)).push(ev.wellId ?? String(ev.text ?? '').split(':')[0]);
  const flush = () => {
    quietTimer = 0; quietAt = performance.now();
    for (const [type, ids] of quietBatch) {
      // re-check at show time: drop wells you've since taken the brake on, or that are no longer held up
      const names = ids.filter((id) => { const w = state?.wells?.[id]; return !w || (w.problem?.type === type && ctx.drill?.wellId !== id); }).map((id) => state?.wells?.[id]?.name ?? id);
      if (!names.length) continue; const lab = QUIET_PROBLEMS[type].label; toast?.show(names.length === 1 ? `${names[0]}: ${lab.toLowerCase()}.` : `${lab}: ${names.slice(0, 4).join(', ')}${names.length > 4 ? ` and ${names.length - 4} more` : ''}.`); }
    quietBatch.clear();
  };
  if (!quietTimer) quietTimer = setTimeout(flush, Math.max(1500, 45000 - (performance.now() - quietAt)));
  return true;
}

function wireBus() {
  // driller's time: tally the driven well's warnings and incidents for the shift report
  const wid = (ev) => ev?.wellId ?? ev?.payload?.wellId ?? null;
  for (const t of ['problem', 'warning']) on(t, (ev) => { if (ctx.drill && wid(ev) === ctx.drill.wellId) ctx.drill.warn++; });
  for (const t of ['blowout', 'accident']) on(t, (ev) => { if (ctx.drill && wid(ev) === ctx.drill.wellId) ctx.drill.incidents++; });
  for (const t of [...EVENTS, ...m2modals.EVENTS, ...m3modals.EVENTS, ...m4modals.EVENTS]) on(t, // M4-UI: m4modals.EVENTS
    perf.timed((ev) => { try { handle(t, ev); } catch (e) { console.error('[ui bus]', t, e); } }, 'ui.' + t)); // render-perf: timed = HUD "UI events" 
}

function wireKeys() {
  window.addEventListener('keydown', (e) => {
    if (e.target.closest('input, textarea, select, [contenteditable]')) return;
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    if (modals.isOpen() && e.key !== 'Escape') return;
    if (e.key === ' ') { e.preventDefault(); ctx.togglePause(); }
    else if (e.key >= '1' && e.key <= '4') ctx.setSpeed(Number(e.key));
    else if (e.key === '0' || e.key === 'p') ctx.pause();
    else if (e.key === 'Escape') modals.closeTop();
    else {
      const route = nav.routeForKey(e.key);
      if (route) ctx.go(route);
    }
    renderDom(true);
  });
}

// ---------- boot ----------
async function boot() {
  pwa.register();
  const apiReady = loadApi({ forceMock: FORCE_MOCK });
  // perf(load): fetch the game shell alongside the sim when the title is skipped, else once the menu can render
  // (so it doesn't compete with the sim chunk on the title's critical path).
  const shellReady = SKIP_TITLE ? loadShell() : apiReady.then(loadShell);
  shellReady.catch(() => {}); // awaited below
  // Title screen: Continue / New Game (charter + prologue) / Load. Resolves with the state to play.
  const pick = await front.runTitle({ apiReady, api, skip: SKIP_TITLE });
  const fresh = pick.fresh;
  state = pick.state;
  notifyDefaults(state);
  // Always start paused so the player sees the screen first.
  state.paused = true;
  if (!state.speed) state.speed = 1;
  let start = 'map';
  try { start = sessionStorage.getItem('driller.route') || 'map'; } catch {}
  // polish G2: a fresh 1859 game opens on the Field, where tutorial step 1 lives.
  if (fresh && (state.startYear ?? 1859) <= 1859) start = 'field';
  if (!PANELS[start]) start = 'map';
  await Promise.all([shellReady, PANELS[start].load().catch(() => {})]); // perf(load): the first panel is in before the reveal

  $('#main').innerHTML = '';
  topbar.mount($('#topbar'), ctx);
  nav.mount($('#nav'), ctx);
  ticker.mount($('#ticker'), ctx);
  toast.mount($('#toasts'), { running: () => ctx.running(), day: () => state?.day ?? null, date: (d) => (d == null ? '' : date(d)), history: () => ctx.go('log') });
  modals.setSink(cardNotice);
  toast.mountInbox($('#tb-bell'));
  modals.mount($('#modal-root'), ctx);
  newspaper.mount(ctx);
  onboarding.mount($('#hint-root'), ctx);
  wireBus();
  wireKeys();
  // dev mode (/dev or ?dev): event-trigger panel on the backtick key
  import('./ui/dev.js').then((m) => { if (m.devRequested()) m.mountDev(ctx); }).catch((e) => console.error('[dev]', e));
  import('./ui/almanacLink.js').then((m) => m.wireAlmanac({ ctx, on, toast })).catch((e) => console.error('[almanac]', e)); // almanac: ⓘ → Almanac routing + first-encounter toasts
  // perf(load): the sound engine is its own chunk, fetched after the game is up (sound?.… is guarded everywhere)
  import('./audio/index.js').then(({ unlockOnGesture, createSoundDirector, mountAudioControl }) => {
    unlockOnGesture();
    sound = createSoundDirector({ on: (t, fn) => on(t, perf.timed(fn, 'sound.' + t)) /* render-perf: HUD "UI events" */, eqDef: (id) => api.data.EQUIPMENT?.[id] ?? null });
    mountAudioControl($('#topbar'), { before: '[data-act="theme"]' });
    ctx.sound = sound;
  }).catch((e) => console.error('[sound] disabled:', e));

  ctx.go(start);
  if (fresh) onboarding.restart(state, ctx);
  // hands-on drilling became the default: switch older saves over once and say how to undo it
  if (state.settings && state.settings.handsOn === undefined) {
    state.settings.handsOn = true;
    if (!fresh) setTimeout(() => toast?.show('Drilling is now hands-on: open a drilling well and you take the brake. Other wells wait for you — or let crews drill unattended in Menu → Settings.', 'info'), 1500);
  }
  if (api.mode === 'mock') toast.show('Dev mock mode: sim modules not loaded.', 'bad');
  modals.checkDecision(state);
  if (fresh) { ctx.save('auto'); ctx.dirty(); } // dirty: let onboarding draw its first hint
  front.dismiss();
  pwa.setBeforeReload(() => { ctx.save('auto'); try { sessionStorage.setItem('driller.resume', '1'); } catch {} });

  setInterval(simStep, 50);
  setInterval(() => { if (ctx.running()) ctx.save('auto'); }, AUTOSAVE_REAL_MS);
  window.addEventListener('beforeunload', () => ctx.save('auto'));
  document.addEventListener('visibilitychange', () => { if (document.hidden) ctx.save('auto'); });
  for (const ev of ['input', 'change', 'focusout']) document.addEventListener(ev, () => { dirty = true; }, true); // panels follow your input while paused
  requestAnimationFrame(frame);
  // perf(load): warm the hub panel (Map: 3D globe + Three.js chunk) once the game is idle, so its first visit is instant
  setTimeout(() => (window.requestIdleCallback ?? ((f) => f()))(() => PANELS.map.load().catch(() => {}), { timeout: 5000 }), 3000);
  window.driller = ctx; // debugging handle
  ctx.perf = perf; perf.init(ctx); // render-perf: HUD toggle (?perf, F3, Settings)
  for (const ev of ['pointermove', 'pointerdown', 'wheel', 'keydown', 'touchstart']) window.addEventListener(ev, () => { lastInput = performance.now(); }, { passive: true, capture: true }); // render-perf: idle throttle
}

boot().catch((e) => {
  console.error(e);
  $('#main').innerHTML = `<div class="boot"><p>Landman failed to start.</p><pre class="small mono">${String(e?.stack ?? e).replace(/</g, '&lt;')}</pre></div>`;
});
